/*
 * UnifiedTeamTab — DEV-1188 Phase 1 (read-only) + Phase 2 (editing).
 *
 * Single shared component for team management across all pillars.
 * Pillar-type aware: layout adapts per pillar.
 *
 * Phase 1 (shipped 2026-06-01):
 *   - Read-only display
 *   - Active Roles + Members + Catalog Reference sections (community)
 *   - Project + mini-project pillars render Phase 2 placeholders
 *
 * Phase 2 (2026-06-01 — items 1-5 from the locked scope):
 *   1. Click slot → assign user / clear / set manual name (modal)
 *   2. Clear an occupied slot back to Vacant (same modal)
 *   3. Edit slot's display title (inline)
 *   4. Reorder slots (up/down arrows)
 *   5. Pool member management (Members section)
 *
 * Phase 3 (DEV-1188 follow-up — items 6-9):
 *   6. Create new slot from catalog
 *   7. Delete a slot
 *   8. Override authoring (Pattern B)
 *   9. Custom role definition authoring
 *
 * Auth model: editing affordances render only when isAdmin === true.
 * The component itself doesn't perform the check — the parent
 * (CommunityManagementTab) derives it from V2 Owner/Admin grants and
 * passes it down. Server-side endpoints enforce their own auth.
 */

import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { useApiEffectiveRoleDefinitions } from '../../api/useApiEffectiveRoleDefinitions';
import {
  deleteRoleSlot,
  fetchRoleSlots,
  upsertRoleSlot,
  fetchDeletedRoleSlots,
  restoreRoleSlot,
  type RoleSlotResponse,
} from '../../api/communityRoleSlots';
import { useApiSecurityData } from '../../api/useApiSecurityData';
import { mediaSrc } from '../../api/mediaUrl';
import { permissionsApiV2 } from '../../security/v2/apiClient';
import type {
  PermissionGrantDto,
  PermissionSetDto,
} from '../../security/v2/types';
// SlotAssignmentModal merged into SlotEditModal as the unified Role tray.
// One surface handles both WHO (linked user / external / vacant) and WHAT
// (display title, photo, contact, catalog link, hidden flag). The legacy
// component is intentionally not imported here anymore.
import SlotEditModal from './SlotEditModal';
import styles from './UnifiedTeamTab.module.css';

export type UnifiedTeamTabViewMode = 'community' | 'project' | 'miniproject';

export interface UnifiedTeamTabProps {
  /** The entity guid (community / project / workspace / mini-project). */
  entityGuid: string;
  /** Pillar type for the catalog filter + layout selection. */
  pillarType: 'geo_community' | 'org_community' | 'full_project' | 'workspace' | 'solution';
  /** Layout family. */
  viewMode: UnifiedTeamTabViewMode;
  /** Whether the viewer can edit. Hides all admin affordances when false. */
  isAdmin?: boolean;
  /** Optional dirty signal. */
  onDirty?: () => void;
}

const MEMBER_BUNDLE_NAME_RE = /^community\s+member$/i;

/**
 * DEV-1195 Phase 1. Compact "x ago" for the Recently Deleted subsection.
 * Matches the relativeTime helper in ContentApprovalsPage. Intentionally
 * simple — for richer formatting we'd reach for date-fns; not worth the
 * dep for one subsection.
 */
function humanizeAgo(date: Date): string {
  const diffMs = Date.now() - date.getTime();
  const mins = Math.floor(diffMs / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return `${Math.floor(days / 30)}mo ago`;
}

const UnifiedTeamTab: React.FC<UnifiedTeamTabProps> = ({
  entityGuid,
  pillarType,
  viewMode,
  isAdmin = false,
}) => {
  const { t } = useAppTranslation('common');

  // DEV-1414 / SCOPING §8.9 (architect-ratified 2026-07-02): org
  // communities have exactly ONE roster surface — the Membership sub-tab.
  // The pool-member section below is display-retired for orgs (replaced
  // by a pointer); the TU-4 pool-grant MECHANISM (issuance, revocation,
  // reconciliation) is untouched. Geo communities keep the section —
  // their member-ops disposition rides the geo self-join chunk.
  const membersDisplayRetired = pillarType === 'org_community';

  // Effective catalog for this entity. COALESCEd source+override.
  const {
    definitions: catalog,
    loading: catalogLoading,
    error: catalogError,
  } = useApiEffectiveRoleDefinitions(entityGuid || null, pillarType);

  // Per-entity slots.
  const [slots, setSlots] = useState<RoleSlotResponse[]>([]);
  const [slotsLoading, setSlotsLoading] = useState<boolean>(viewMode === 'community');
  const [slotsError, setSlotsError] = useState<string | null>(null);
  const [rowBusy, setRowBusy] = useState<Record<string, boolean>>({});
  // DEV-1188 Phase 2 (unified Role tray, 2026-06-01): single slot-edit
  // state for both the per-row pencil and the "Add Role" header button.
  // When editingSlot is a scaffolded blank slot (not in `slots`), the
  // SlotEditModal save creates a new row. The isCreating flag drives the
  // tray title override.
  const [editingSlot, setEditingSlot] = useState<RoleSlotResponse | null>(null);
  const [isCreatingSlot, setIsCreatingSlot] = useState<boolean>(false);
  // DEV-1188 Phase 2 item 4 — drag-and-drop reorder. Mirrors the native
  // HTML5 pattern in ProjectTeamPanel.tsx (no library dependency).
  const [dragSlotGuid, setDragSlotGuid] = useState<string | null>(null);
  const [dragOverSlotGuid, setDragOverSlotGuid] = useState<string | null>(null);

  // DEV-1195 Phase 1 — soft-deleted slots ("Recently deleted" subsection).
  // First N rows always visible (progressive disclosure beats a hidden
  // section behind a tiny chevron — Skip's UX feedback 2026-06-02).
  // Restore re-fires loadSlots + loadDeletedSlots to keep both sides in
  // sync.
  const [deletedSlots, setDeletedSlots] = useState<RoleSlotResponse[]>([]);
  const [showAllDeleted, setShowAllDeleted] = useState<boolean>(false);
  const [restoreBusy, setRestoreBusy] = useState<Record<string, boolean>>({});

  // V2 grants + permission sets for the Members section.
  const [grants, setGrants] = useState<PermissionGrantDto[]>([]);
  const [permissionSets, setPermissionSets] = useState<PermissionSetDto[]>([]);
  const [poolLoading, setPoolLoading] = useState<boolean>(viewMode === 'community');
  const [poolError, setPoolError] = useState<string | null>(null);
  const [poolPickerOpen, setPoolPickerOpen] = useState<boolean>(false);
  // DEV: poolPickUserGuid retired 2026-06-02 — picker now single-click,
  // commit happens inline from the row onClick.
  const [poolUserSearch, setPoolUserSearch] = useState<string>('');
  const [poolBusy, setPoolBusy] = useState<boolean>(false);

  // Platform user roster.
  const { securityUsers } = useApiSecurityData();

  const loadSlots = useCallback(async () => {
    if (viewMode !== 'community' || !entityGuid) {
      setSlots([]);
      setSlotsLoading(false);
      return;
    }
    setSlotsLoading(true);
    setSlotsError(null);
    try {
      const rows = await fetchRoleSlots(entityGuid);
      setSlots(rows);
    } catch (err) {
      setSlotsError(err instanceof Error ? err.message : String(err));
    } finally {
      setSlotsLoading(false);
    }
  }, [entityGuid, viewMode]);

  // DEV-1195 Phase 1 — load the deleted-slots list. Cheap query (returns
  // only soft-deleted rows for this community), so we fire it alongside
  // the active load rather than gating behind expand.
  const loadDeletedSlots = useCallback(async () => {
    if (viewMode !== 'community' || !entityGuid) {
      setDeletedSlots([]);
      return;
    }
    try {
      const rows = await fetchDeletedRoleSlots(entityGuid);
      setDeletedSlots(rows);
    } catch {
      // Non-fatal — the active list is the primary UX. If the trash
      // query fails we just don't show the section's count.
      setDeletedSlots([]);
    }
  }, [entityGuid, viewMode]);

  useEffect(() => {
    loadSlots();
    loadDeletedSlots();
  }, [loadSlots, loadDeletedSlots]);

  // Pool loader hoisted above the refresh-listener useEffect so that
  // effect can include it in its deps. (Skip UX feedback 2026-06-02:
  // pool needs to refresh on l4e_security_updated when an invitee
  // accepts in another session, so the Pending badge clears.)
  const loadPool = useCallback(async () => {
    if (viewMode !== 'community' || !entityGuid) {
      setGrants([]);
      setPermissionSets([]);
      setPoolLoading(false);
      return;
    }
    setPoolLoading(true);
    setPoolError(null);
    try {
      const [g, ps] = await Promise.all([
        permissionsApiV2.listGrantsForScope('community', entityGuid),
        permissionsApiV2.permissionSets(),
      ]);
      setGrants(g);
      setPermissionSets(ps);
    } catch (err) {
      setPoolError(err instanceof Error ? err.message : String(err));
    } finally {
      setPoolLoading(false);
    }
  }, [entityGuid, viewMode]);

  useEffect(() => {
    loadPool();
  }, [loadPool]);

  // DEV-1195 Phase 1 — restore handler. Re-fires both loads so the slot
  // moves from "Recently deleted" back into Active Roles in one render.
  const handleRestore = useCallback(async (roleSlotGuid: string) => {
    setRestoreBusy((m) => ({ ...m, [roleSlotGuid]: true }));
    try {
      await restoreRoleSlot(roleSlotGuid);
      await Promise.all([loadSlots(), loadDeletedSlots()]);
      // Notify cross-tab listeners (Engagements page slot cards, etc.).
      window.dispatchEvent(
        new CustomEvent('l4e_community_enablers_persisted', {
          detail: { communityGuid: entityGuid },
        }),
      );
    } catch (err) {
      setSlotsError(err instanceof Error ? err.message : String(err));
    } finally {
      setRestoreBusy((m) => {
        const next = { ...m };
        delete next[roleSlotGuid];
        return next;
      });
    }
  }, [entityGuid, loadSlots, loadDeletedSlots]);

  // ── DEV-1215 follow-up (2026-06-05): vacant-slot cleanup ────────
  //
  // Row-level × delete for a single vacant slot, and a bulk "Clear all
  // vacant" button for the cleanup pass Skip is doing to make way for
  // the Role Bundle model. Both call deleteRoleSlot (soft-delete via
  // DEV-1195 — restore lives in Recently Deleted for 30 days).
  //
  // Why this exists alongside SlotEditModal's Remove Role button: the
  // modal hides delete behind an "Assign" entry point, which is the
  // wrong mental model for cleanup intent. These give admins a direct
  // path: ✕ for one, Clear all for the section.

  const handleDeleteVacantSlot = useCallback(async (roleSlotGuid: string, label: string) => {
    if (!confirm(
      (t('unifiedTeamTab.confirmDeleteVacant', {
        defaultValue: 'Delete the "{{role}}" role? It will go to Recently Deleted and can be restored within 30 days.',
        role: label,
      }) as string),
    )) return;
    setRowBusy((m) => ({ ...m, [roleSlotGuid]: true }));
    try {
      await deleteRoleSlot(roleSlotGuid);
      await Promise.all([loadSlots(), loadDeletedSlots()]);
      window.dispatchEvent(
        new CustomEvent('l4e_community_enablers_persisted', {
          detail: { communityGuid: entityGuid },
        }),
      );
    } catch (err) {
      setSlotsError(err instanceof Error ? err.message : String(err));
    } finally {
      setRowBusy((m) => {
        const next = { ...m };
        delete next[roleSlotGuid];
        return next;
      });
    }
  }, [entityGuid, loadSlots, loadDeletedSlots, t]);

  const [clearingVacant, setClearingVacant] = useState(false);

  const handleClearAllVacant = useCallback(async (vacantSlots: RoleSlotResponse[]) => {
    if (vacantSlots.length === 0 || clearingVacant) return;
    if (!confirm(
      (t('unifiedTeamTab.confirmClearAllVacant', {
        defaultValue: 'Soft-delete all {{count}} vacant slots? They go to Recently Deleted and can be restored individually within 30 days.',
        count: vacantSlots.length,
      }) as string),
    )) return;
    setClearingVacant(true);
    try {
      // Sequential — backend has no batch endpoint, and the role-slot
      // count per community is small enough that the latency is fine.
      for (const slot of vacantSlots) {
        await deleteRoleSlot(slot.roleSlotGuid);
      }
      await Promise.all([loadSlots(), loadDeletedSlots()]);
      window.dispatchEvent(
        new CustomEvent('l4e_community_enablers_persisted', {
          detail: { communityGuid: entityGuid },
        }),
      );
    } catch (err) {
      setSlotsError(err instanceof Error ? err.message : String(err));
      // Refresh anyway to pick up whatever partial state landed.
      await Promise.all([loadSlots(), loadDeletedSlots()]);
    } finally {
      setClearingVacant(false);
    }
  }, [clearingVacant, entityGuid, loadSlots, loadDeletedSlots, t]);

  // DEV-1193 follow-up: refresh slot list when something else mutates
  // (SlotEditModal save / revoke / delete all dispatch
  // l4e_community_enablers_persisted). Plus the SignalR-driven
  // l4e_security_updated event — fires on this session when ANOTHER
  // user accepts/declines/resigns/revokes (e.g., RISSAR accepting in
  // their own browser triggers SecurityUpdated on Skip's session via
  // SignalR → signalrConnection.ts re-dispatches as l4e_security_updated).
  // Without these, an invitee accepting in their session leaves the
  // inviter's slot card stuck at "Pending" until hard reload.
  useEffect(() => {
    const onRefresh = (e: Event) => {
      const detail = (e as CustomEvent<{ communityGuid?: string }>).detail;
      const refresh = () => {
        loadSlots();
        // DEV-1195: keep the Recently Deleted count fresh when a slot
        // is soft-deleted via SlotEditModal (which dispatches this same
        // event after a Remove).
        loadDeletedSlots();
        // Skip UX feedback 2026-06-02: the Members section also has to
        // react to its own mutations (pending → active transitions on
        // accept, removals, etc.). Without this the Pending badge sits
        // stale until the admin reloads.
        loadPool();
      };
      if (!detail || !detail.communityGuid) {
        refresh();
        return;
      }
      if (detail.communityGuid.toUpperCase() === (entityGuid || '').toUpperCase()) {
        refresh();
      }
    };
    // SignalR SecurityUpdated → broad refresh (no community filter
    // because the payload doesn't carry communityGuid; fine to
    // re-fetch on every event since UnifiedTeamTab is mounted only
    // when the user is actively on the Team & Roles surface).
    // Skip UX feedback 2026-06-02: include loadPool() so the Members
    // section's Pending badge clears when the invitee accepts in
    // another session.
    const onSecurityUpdated = () => {
      loadSlots();
      loadDeletedSlots();
      loadPool();
    };
    window.addEventListener('l4e_community_enablers_persisted', onRefresh);
    window.addEventListener('l4e_security_updated', onSecurityUpdated);
    return () => {
      window.removeEventListener('l4e_community_enablers_persisted', onRefresh);
      window.removeEventListener('l4e_security_updated', onSecurityUpdated);
    };
  }, [loadSlots, loadDeletedSlots, loadPool, entityGuid]);

  // Catalog-by-guid for slot enrichment.
  const catalogByGuid = useMemo(() => {
    const m = new Map<string, (typeof catalog)[number]>();
    for (const d of catalog) m.set(d.definitionGuid.toLowerCase(), d);
    return m;
  }, [catalog]);

  // DEV-1196: count active slots per catalog definitionGuid so the
  // catalog row can render "Added (N)" when one or more slots in
  // Active Roles point at this definition.
  const slotCountByDefinitionGuid = useMemo(() => {
    const m = new Map<string, number>();
    for (const s of slots) {
      if (s.sourceDefinitionGuid) {
        const k = s.sourceDefinitionGuid.toLowerCase();
        m.set(k, (m.get(k) ?? 0) + 1);
      }
    }
    return m;
  }, [slots]);

  // DEV-1196: search filter for the promoted-to-top catalog list.
  const [catalogSearchQuery, setCatalogSearchQuery] = useState<string>('');
  const filteredCatalog = useMemo(() => {
    const q = catalogSearchQuery.trim().toLowerCase();
    if (!q) return catalog;
    return catalog.filter((d) => {
      const hay = `${d.label} ${d.description ?? ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [catalog, catalogSearchQuery]);

  // Find the "Community Member" permission set.
  const memberBundle = useMemo<PermissionSetDto | null>(() => {
    return permissionSets.find((p) => MEMBER_BUNDLE_NAME_RE.test(p.name)) || null;
  }, [permissionSets]);

  // user_guid → user row.
  const userByGuid = useMemo(() => {
    const m = new Map<string, Record<string, any>>();
    for (const u of securityUsers || []) {
      m.set(String(u.user_guid || '').toUpperCase(), u);
    }
    return m;
  }, [securityUsers]);

  // Pool = active+pending, non-slot-anchored Member grants.
  const poolGrants = useMemo<PermissionGrantDto[]>(() => {
    if (!memberBundle) return [];
    const memberGuid = memberBundle.permissionSetGuid.toUpperCase();
    return grants.filter(
      (g) =>
        g.permissionSetGuid.toUpperCase() === memberGuid &&
        !g.roleSlotGuid &&
        (g.status === 'active' || g.status === 'pending')
    );
  }, [grants, memberBundle]);

  // ── Mutation handlers ─────────────────────────────────────────
  //
  // Title editing moved out of inline-pencil form (2026-06-01 redesign).
  // The "Edit Role" button now opens SlotEditModal which handles display
  // title, hidden flag, and catalog linkage in one place.

  // ── Drag-and-drop reorder ─────────────────────────────────────
  // Native HTML5 D&D, no library. Mirrors ProjectTeamPanel.tsx pattern.

  const handleDragStart = useCallback(
    (slotGuid: string) => (e: React.DragEvent) => {
      setDragSlotGuid(slotGuid);
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', slotGuid);
    },
    []
  );

  const handleDragEnd = useCallback(() => {
    setDragSlotGuid(null);
    setDragOverSlotGuid(null);
  }, []);

  const handleDragOver = useCallback(
    (slotGuid: string) => (e: React.DragEvent) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      setDragOverSlotGuid(slotGuid);
    },
    []
  );

  const handleDragLeave = useCallback(() => {
    setDragOverSlotGuid(null);
  }, []);

  const handleDrop = useCallback(
    (targetSlotGuid: string) => async (e: React.DragEvent) => {
      e.preventDefault();
      setDragSlotGuid(null);
      setDragOverSlotGuid(null);

      const sourceGuid = e.dataTransfer.getData('text/plain');
      if (!sourceGuid || sourceGuid === targetSlotGuid) return;

      // Read current snapshot.
      const sorted = slots.slice().sort((a, b) => a.sortOrder - b.sortOrder);
      const sourceIdx = sorted.findIndex(
        (s) => s.roleSlotGuid.toUpperCase() === sourceGuid.toUpperCase()
      );
      const targetIdx = sorted.findIndex(
        (s) => s.roleSlotGuid.toUpperCase() === targetSlotGuid.toUpperCase()
      );
      if (sourceIdx === -1 || targetIdx === -1) return;

      // Reorder: remove source, insert at target position.
      const reordered = [...sorted];
      const [moved] = reordered.splice(sourceIdx, 1);
      reordered.splice(targetIdx, 0, moved);

      // Identify the affected range — only slots whose new sort_order
      // differs from the old. Avoids needless server roundtrips.
      const affected = reordered
        .map((s, idx) => ({ slot: s, newSortOrder: idx }))
        .filter(({ slot, newSortOrder }) => slot.sortOrder !== newSortOrder);

      if (affected.length === 0) return;

      // Mark all affected rows busy.
      setRowBusy((m) => {
        const next = { ...m };
        for (const { slot } of affected) next[slot.roleSlotGuid] = true;
        return next;
      });

      try {
        const saved = await Promise.all(
          affected.map(({ slot, newSortOrder }) =>
            upsertRoleSlot(slot.roleSlotGuid, entityGuid, {
              roleSlotGuid: slot.roleSlotGuid,
              displayTitle: slot.displayTitle,
              sortOrder: newSortOrder,
              isHidden: slot.isHidden,
              linkedUserGuid: slot.linkedUserGuid,
              manualName: slot.manualName,
              manualTitle: slot.manualTitle,
              manualPhotoUrl: slot.manualPhotoUrl,
              manualEmail: slot.manualEmail,
              manualPhone: slot.manualPhone,
              externalProfileUrl: slot.externalProfileUrl,
              notes: slot.notes,
              sourceDefinitionGuid: slot.sourceDefinitionGuid,
            })
          )
        );
        // Merge saved rows back into state by guid.
        const byGuid = new Map(saved.map((s) => [s.roleSlotGuid, s]));
        setSlots((all) =>
          all.map((s) => byGuid.get(s.roleSlotGuid) ?? s)
        );
        // Notify Alignment-tab CommunityEnablers to refresh ordering.
        // Without this, the public leader grid keeps the old order
        // until reload. Contract: communityEnablers.ts notifyPersisted().
        window.dispatchEvent(
          new CustomEvent('l4e_community_enablers_persisted', {
            detail: { communityGuid: entityGuid },
          }),
        );
      } catch (err) {
        setSlotsError(err instanceof Error ? err.message : String(err));
      } finally {
        setRowBusy((m) => {
          const next = { ...m };
          for (const { slot } of affected) delete next[slot.roleSlotGuid];
          return next;
        });
      }
    },
    [slots, entityGuid]
  );

  // ── Add Role (create flow) ────────────────────────────────────
  // Scaffold a fresh blank slot, open the unified SlotEditModal in
  // create mode. Save POSTs via upsertRoleSlot (a brand-new
  // roleSlotGuid signals create). Admin picks "Who fills this role?"
  // (Platform / External / Vacant) inside the tray.
  const handleAddRole = useCallback(() => {
    const nextSortOrder = slots.length === 0
      ? 0
      : Math.max(...slots.map((s) => s.sortOrder)) + 1;
    const nowIso = new Date().toISOString();
    const newGuid =
      typeof globalThis.crypto?.randomUUID === 'function'
        ? globalThis.crypto.randomUUID()
        : // RFC 4122 v4 fallback for browsers without crypto.randomUUID.
          'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
            const r = (Math.random() * 16) | 0;
            const v = c === 'x' ? r : (r & 0x3) | 0x8;
            return v.toString(16);
          });
    const scaffold: RoleSlotResponse = {
      roleSlotGuid: newGuid,
      communityEntityGuid: entityGuid,
      displayTitle: '',
      sortOrder: nextSortOrder,
      isHidden: false,
      linkedUserGuid: null,
      manualName: null,
      manualTitle: null,
      manualPhotoUrl: null,
      manualEmail: null,
      manualPhone: null,
      externalProfileUrl: null,
      notes: null,
      // DEV-1159 Phase A — role-definition fields default empty/null
      description: null,
      skillsRequired: [],
      timeCommitment: null,
      permissionSetGuid: null,
      sourceDefinitionGuid: null,
      openAssignment: null,
      createdAt: nowIso,
      updatedAt: nowIso,
      // DEV-1195 — scaffolds are always active; trash fields stay null.
      deletedAt: null,
      deletedByUserGuid: null,
    };
    setEditingSlot(scaffold);
    setIsCreatingSlot(true);
  }, [slots, entityGuid]);

  // ── DEV-1196: Add from catalog ─────────────────────────────────
  // Scaffold a slot pre-populated from a catalog role definition.
  // Inherits label, description, skills, time commitment, permission
  // bundle, and sourceDefinitionGuid (which gates rhythm
  // materialization on first occupant assignment per DEV-1145).
  // Opens the unified SlotEditModal in create mode, same as
  // handleAddRole — admin only needs to pick the occupant.
  const handleAddFromCatalog = useCallback(
    (catalogEntry: (typeof catalog)[number]) => {
      const nextSortOrder = slots.length === 0
        ? 0
        : Math.max(...slots.map((s) => s.sortOrder)) + 1;
      const nowIso = new Date().toISOString();
      const newGuid =
        typeof globalThis.crypto?.randomUUID === 'function'
          ? globalThis.crypto.randomUUID()
          : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
              const r = (Math.random() * 16) | 0;
              const v = c === 'x' ? r : (r & 0x3) | 0x8;
              return v.toString(16);
            });
      const scaffold: RoleSlotResponse = {
        roleSlotGuid: newGuid,
        communityEntityGuid: entityGuid,
        displayTitle: catalogEntry.label,
        sortOrder: nextSortOrder,
        isHidden: false,
        linkedUserGuid: null,
        manualName: null,
        manualTitle: null,
        manualPhotoUrl: null,
        manualEmail: null,
        manualPhone: null,
        externalProfileUrl: null,
        notes: null,
        description: catalogEntry.description ?? null,
        skillsRequired: catalogEntry.skillsSuggested ?? [],
        timeCommitment: catalogEntry.timeCommitmentHint ?? null,
        permissionSetGuid: catalogEntry.permissionSetGuid ?? null,
        sourceDefinitionGuid: catalogEntry.definitionGuid,
        openAssignment: null,
        createdAt: nowIso,
        updatedAt: nowIso,
        // DEV-1195 — scaffolds are always active; trash fields stay null.
        deletedAt: null,
        deletedByUserGuid: null,
      };
      setEditingSlot(scaffold);
      setIsCreatingSlot(true);
    },
    [slots, entityGuid],
  );

  /**
   * Skip UX feedback 2026-06-02: single-click on a search result fires
   * the add immediately. The previous two-step pattern (click row to
   * pick, click "Add member" to commit) confused users — clicking a
   * result didn't fill a "name field" because there was no such field;
   * the picked state only highlighted the row.
   *
   * Also emits a community.role_assignment workflow_request when the
   * grant comes back pending. Without this, the invitee never sees an
   * invitation in their Action Center — the grant sits pending forever
   * while the admin's UI says "Pending" with no path to acceptance.
   * Mirrors the pattern in CommunityPermissionsV2Panel (DEV-892).
   */
  const handlePoolAdd = async (userGuid: string) => {
    if (!memberBundle || !userGuid || poolBusy) return;
    setPoolBusy(true);
    setPoolError(null);
    try {
      const grant = await permissionsApiV2.createGrantForScope(
        'community',
        entityGuid,
        {
          permissionSetGuid: memberBundle.permissionSetGuid,
          userGuid,
        },
      );

      // DEV-892 follow-up (was missing on this path; fix Skip 2026-06-02).
      // Emit the workflow_request so the invitee's Action Center surfaces
      // the role-invitation card with Accept / Decline. Self-assignments
      // come back Active and skip this path.
      if (grant.status === 'pending') {
        try {
          const { addOrUpdateWorkflow, submitRequest } = await import('../../workflow');
          const { getCurrentUserGuid } = await import('../../api/client');
          const inviter = getCurrentUserGuid();
          const invitee = securityUsers?.find(
            (u) => String(u.user_guid || '').toUpperCase() === userGuid.toUpperCase(),
          );
          const inviteeName =
            invitee?.display_name || invitee?.name || invitee?.email || 'User';
          const bundleName = memberBundle.name || 'Member';
          const now = new Date();
          const makeGuid = (): string => {
            try {
              const c: any = (globalThis as any).crypto;
              if (c?.randomUUID) return String(c.randomUUID());
            } catch { /* fall through */ }
            return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
              const r = (Math.random() * 16) | 0;
              const v = c === 'x' ? r : (r & 0x3) | 0x8;
              return v.toString(16);
            });
          };
          const draft = {
            workflow_guid: makeGuid(),
            target_type: 'community' as const,
            target_guid: entityGuid,
            action_type: 'community.role_assignment',
            title: `${bundleName} invitation`,
            description: `You've been invited to take the ${bundleName} role.`,
            payload: {
              community_guid: entityGuid,
              user_guid: userGuid,
              permission_set_guid: memberBundle.permissionSetGuid,
              grant_guid: grant.grantGuid,
              role: bundleName,
              user_display_name: inviteeName,
              custom_message: '',
            },
            status: 'draft' as const,
            version: 1,
            created_by: String(inviter || ''),
            created_at: now.toISOString(),
            updated_at: now.toISOString(),
            assigned_to_guids: [userGuid],
            history: [
              { action: 'draft' as const, by: String(inviter || ''), at: now.toISOString() },
            ],
          };
          const submitted = submitRequest(draft as any, String(inviter || ''));
          addOrUpdateWorkflow(submitted);
        } catch (wfErr) {
          // Non-fatal: the grant is still pending, an admin can resend
          // via CommunityPermissionsV2Panel. Log so it's visible.
          console.warn('[UnifiedTeamTab] member workflow emit failed:', wfErr);
        }
      }

      setPoolPickerOpen(false);
      setPoolUserSearch('');
      await loadPool();
    } catch (err) {
      setPoolError(err instanceof Error ? err.message : String(err));
    } finally {
      setPoolBusy(false);
    }
  };

  const handlePoolRemove = async (g: PermissionGrantDto) => {
    if (poolBusy) return;
    const u = userByGuid.get(String(g.userGuid || '').toUpperCase());
    const userName = u?.display_name || u?.name ||
      t('unifiedTeamTab.thisUser', { defaultValue: 'this user' });
    if (
      !confirm(
        t('unifiedTeamTab.confirmRemovePool', {
          defaultValue: 'Remove {{name}} from the community pool?',
          name: userName,
        }) as string
      )
    )
      return;
    setPoolBusy(true);
    setPoolError(null);
    try {
      await permissionsApiV2.revokeGrant(g.grantGuid);
      await loadPool();
    } catch (err) {
      setPoolError(err instanceof Error ? err.message : String(err));
    } finally {
      setPoolBusy(false);
    }
  };

  const existingPoolUserGuids = useMemo(() => {
    const s = new Set<string>();
    for (const g of poolGrants) {
      if (g.userGuid) s.add(g.userGuid.toUpperCase());
    }
    return s;
  }, [poolGrants]);

  // Search-driven picker — empty query returns empty list (admin sees
  // "Start typing to search" hint), matching MiniProjectTeamTab pattern.
  // Avoids dumping the entire user roster on first focus.
  const filteredPoolUsers = useMemo(() => {
    const q = poolUserSearch.trim().toLowerCase();
    if (!q) return [];
    const pool = (securityUsers || []).filter(
      (u) => !existingPoolUserGuids.has(String(u.user_guid || '').toUpperCase())
    );
    return pool
      .filter((u) =>
        [u.display_name, u.name, u.email, u.screen_name]
          .filter(Boolean)
          .some((s: string) => String(s).toLowerCase().includes(q))
      )
      .slice()
      .sort((a, b) =>
        String(a.display_name || a.name || a.email || '').localeCompare(
          String(b.display_name || b.name || b.email || ''),
          undefined,
          { numeric: true, sensitivity: 'base' }
        )
      )
      .slice(0, 50);
  }, [securityUsers, poolUserSearch, existingPoolUserGuids]);

  // Phase 1: project + mini-project pillars render placeholders only.
  if (viewMode === 'project') {
    return (
      <div className={styles.container}>
        <div className={styles.placeholder}>
          {t('unifiedTeamTab.projectPhase2Placeholder')}
        </div>
      </div>
    );
  }
  if (viewMode === 'miniproject') {
    return (
      <div className={styles.container}>
        <div className={styles.placeholder}>
          {t('unifiedTeamTab.miniProjectPhase2Placeholder')}
        </div>
      </div>
    );
  }

  // Community pillar.
  const loading = catalogLoading || slotsLoading;
  const error = catalogError || slotsError;
  const sortedSlots = slots.slice().sort((a, b) => a.sortOrder - b.sortOrder);

  return (
    <div className={styles.container}>
      {/* Preview banner retired 2026-06-01 — the component is no longer
          read-only; the banner copy ("editing lands in later phases")
          is now stale. */}

      {loading && (
        <div className={styles.statusRow}>{t('unifiedTeamTab.loading')}</div>
      )}

      {error && (
        <div className={styles.errorRow}>
          {t('unifiedTeamTab.errorPrefix')}: {error}
        </div>
      )}

      {!loading && !error && (
        <>
          {/* ── DEV-1196: Catalog (promoted to top, action-enabled) ──
              Catalog of role definitions available for this entity.
              Each entry has a + Add button that creates a slot
              pre-populated with the catalog role's fields (including
              sourceDefinitionGuid, which gates rhythm materialization).
              An "Added (N)" badge appears when one or more Active Roles
              already point at this catalog entry — informational only,
              admin can still add another instance (multi-occupant slots
              are allowed; see DEV-1196 design Q1 lock).
              Custom (non-catalog) slot creation moves to a dedicated
              header button. */}
          <section className={styles.section}>
            <header className={styles.sectionHeader}>
              <h3 className={styles.sectionTitle}>
                {t('unifiedTeamTab.sectionCatalogReference')}
              </h3>
              <span className={styles.sectionCount}>{catalog.length}</span>
              {isAdmin && (
                <button
                  type="button"
                  className={styles.headerAction}
                  onClick={handleAddRole}
                  title={t('unifiedTeamTab.addCustomRoleHelp', {
                    defaultValue: 'Add a one-off role not in the catalog',
                  }) as string}
                >
                  {t('unifiedTeamTab.addCustomRoleBtn', {
                    defaultValue: '+ Add Custom Role',
                  })}
                </button>
              )}
            </header>
            {catalog.length === 0 ? (
              <div className={styles.empty}>
                {t('unifiedTeamTab.catalogEmpty')}
              </div>
            ) : (
              <>
                <div className={styles.catalogSearchRow}>
                  <input
                    type="search"
                    className={styles.catalogSearch}
                    value={catalogSearchQuery}
                    onChange={(e) => setCatalogSearchQuery(e.target.value)}
                    placeholder={t('unifiedTeamTab.catalogSearchPlaceholder', {
                      defaultValue: 'Search roles…',
                    }) as string}
                  />
                </div>
                {filteredCatalog.length === 0 ? (
                  <div className={styles.empty}>
                    {t('unifiedTeamTab.catalogSearchNoMatch', {
                      defaultValue: 'No catalog roles match your search.',
                    })}
                  </div>
                ) : (
                  <ul className={styles.catalogList}>
                    {filteredCatalog.map((d) => {
                      const addedCount =
                        slotCountByDefinitionGuid.get(d.definitionGuid.toLowerCase()) ?? 0;
                      return (
                        <li key={d.definitionGuid} className={styles.catalogRow}>
                          <span className={styles.catalogLabel}>{d.label}</span>
                          {d.hasOverride && (
                            <span className={styles.badgeOverride}>
                              {t('unifiedTeamTab.badgeOverride')}
                            </span>
                          )}
                          {d.roleRhythmGuid && (
                            <span className={styles.badgeRhythm}>
                              {t('unifiedTeamTab.badgeRhythm')}
                            </span>
                          )}
                          {addedCount > 0 && (
                            <span className={styles.badgeAdded}>
                              {t('unifiedTeamTab.badgeAdded', {
                                defaultValue: 'Added',
                                count: addedCount,
                              })}
                              {addedCount > 1 ? ` (${addedCount})` : ''}
                            </span>
                          )}
                          {isAdmin && (
                            <button
                              type="button"
                              className={styles.catalogAddBtn}
                              onClick={() => handleAddFromCatalog(d)}
                              title={t('unifiedTeamTab.addFromCatalogTooltip', {
                                defaultValue: 'Add to Active Roles',
                              }) as string}
                              aria-label={t('unifiedTeamTab.addFromCatalogTooltip', {
                                defaultValue: 'Add to Active Roles',
                              }) as string}
                            >
                              +
                            </button>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </>
            )}
          </section>

          {/* ── Active Roles section ─────────────────────────────── */}
          <section className={styles.section}>
            <header className={styles.sectionHeader}>
              <h3 className={styles.sectionTitle}>
                {t('unifiedTeamTab.sectionActiveRoles')}
              </h3>
              <span className={styles.sectionCount}>{slots.length}</span>
              {/* DEV-1196: legacy "Add Role" button retired. Creation now
                  flows from the Catalog section above (+ Add per entry)
                  or its "+ Add Custom Role" header button. */}
              {/* DEV-1215 follow-up — "Clear all vacant" bulk button.
                  Hidden unless admin AND there's at least one vacant
                  slot to clear. Useful for cleaning up legacy seeded
                  slots before applying a Role Bundle. */}
              {isAdmin && (() => {
                const vacantSlots = slots.filter(
                  (s) => !s.linkedUserGuid && !s.manualName,
                );
                if (vacantSlots.length === 0) return null;
                return (
                  <button
                    type="button"
                    onClick={() => void handleClearAllVacant(vacantSlots)}
                    disabled={clearingVacant}
                    title={t('unifiedTeamTab.clearAllVacantTitle', {
                      defaultValue: 'Soft-delete every vacant slot in this section. They go to Recently Deleted and can be restored.',
                    }) as string}
                    style={{
                      marginInlineStart: 'auto',
                      padding: '4px 10px',
                      background: 'transparent',
                      color: 'var(--color-error)',
                      border: '1px solid var(--color-error)',
                      borderRadius: 4,
                      fontSize: 12,
                      cursor: clearingVacant ? 'wait' : 'pointer',
                    }}
                  >
                    {clearingVacant
                      ? (t('unifiedTeamTab.clearingVacant', { defaultValue: 'Clearing…' }) as string)
                      : (t('unifiedTeamTab.clearAllVacantBtn', {
                          defaultValue: 'Clear all vacant ({{count}})',
                          count: vacantSlots.length,
                        }) as string)}
                  </button>
                );
              })()}
            </header>

            {slots.length === 0 ? (
              <div className={styles.empty}>
                {t('unifiedTeamTab.activeRolesEmpty')}
              </div>
            ) : (
              <ul className={styles.slotList}>
                {sortedSlots.map((slot) => {
                  const catalogEntry = slot.sourceDefinitionGuid
                    ? catalogByGuid.get(slot.sourceDefinitionGuid.toLowerCase())
                    : null;
                  const effectiveLabel = catalogEntry?.label ?? slot.displayTitle;
                  const hasRhythm = !!catalogEntry?.roleRhythmGuid;
                  const hasOverride = !!catalogEntry?.hasOverride;
                  const linkedUser = slot.linkedUserGuid
                    ? userByGuid.get(slot.linkedUserGuid.toUpperCase())
                    : null;
                  const occupantName =
                    slot.manualName ||
                    linkedUser?.display_name ||
                    linkedUser?.name ||
                    '';
                  // Avatar source priority:
                  //   1. slot.manualPhotoUrl (admin-uploaded for figurehead)
                  //   2. linked user's profile_image_url
                  //   3. initials fallback
                  const photoUrl = mediaSrc(
                    slot.manualPhotoUrl ||
                    linkedUser?.profile_image_url ||
                    null,
                  );
                  const initials = (occupantName || effectiveLabel || '?')
                    .split(/\s+/)
                    .filter(Boolean)
                    .slice(0, 2)
                    .map((w) => w[0]?.toUpperCase() || '')
                    .join('') || '?';
                  const isBusy = !!rowBusy[slot.roleSlotGuid];
                  const isBeingDragged = dragSlotGuid === slot.roleSlotGuid;
                  const isDragOver =
                    dragOverSlotGuid === slot.roleSlotGuid &&
                    dragSlotGuid !== slot.roleSlotGuid;

                  return (
                    <li
                      key={slot.roleSlotGuid}
                      className={[
                        styles.slotRow,
                        isBeingDragged ? styles.slotRowDragging : '',
                        isDragOver ? styles.slotRowDragOver : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      draggable={isAdmin && !isBusy}
                      onDragStart={
                        isAdmin ? handleDragStart(slot.roleSlotGuid) : undefined
                      }
                      onDragEnd={isAdmin ? handleDragEnd : undefined}
                      onDragOver={
                        isAdmin ? handleDragOver(slot.roleSlotGuid) : undefined
                      }
                      onDragLeave={isAdmin ? handleDragLeave : undefined}
                      onDrop={
                        isAdmin ? handleDrop(slot.roleSlotGuid) : undefined
                      }
                    >
                      {isAdmin ? (
                        <span
                          className={styles.dragHandle}
                          title={t('unifiedTeamTab.dragToReorderTooltip', { defaultValue: 'Drag to reorder' }) as string}
                          aria-label={t('unifiedTeamTab.dragToReorderTooltip', { defaultValue: 'Drag to reorder' }) as string}
                        >
                          ⋮⋮
                        </span>
                      ) : (
                        <span className={styles.dragHandle} aria-hidden="true" />
                      )}

                      {photoUrl ? (
                        <img
                          src={photoUrl}
                          alt=""
                          className={styles.slotAvatarImg}
                        />
                      ) : (
                        <div className={styles.slotAvatar}>{initials}</div>
                      )}

                      <div className={styles.slotLabelCol}>
                        <span className={styles.slotLabel}>
                          {effectiveLabel}
                          <span className={styles.slotBadges}>
                            {hasRhythm && (
                              <span className={styles.badgeRhythm}>
                                {t('unifiedTeamTab.badgeRhythm')}
                              </span>
                            )}
                            {hasOverride && (
                              <span className={styles.badgeOverride}>
                                {t('unifiedTeamTab.badgeOverride')}
                              </span>
                            )}
                            {/* DEV-1193 Phase 3d: pending invitation badge.
                                Surfaces when admin has invited a user but
                                the invitee hasn't accepted yet. Distinct
                                from the Added/Rhythm badges; uses warning
                                color to draw the eye for follow-up. */}
                            {slot.openAssignment?.status === 'pending_invitation' && (
                              <span className={styles.badgePending} title={
                                slot.openAssignment.userDisplayName
                                  ? `${t('unifiedTeamTab.badgePending', { defaultValue: 'Pending' })}: ${slot.openAssignment.userDisplayName}`
                                  : t('unifiedTeamTab.badgePending', { defaultValue: 'Pending' }) as string
                              }>
                                {t('unifiedTeamTab.badgePending', { defaultValue: 'Pending' })}
                              </span>
                            )}
                          </span>
                        </span>
                        {/* DEV-1193 Phase 3d: when an invitation is pending,
                            surface the invitee's name as the occupant line
                            so the admin sees who they invited without
                            opening the editor. */}
                        {slot.openAssignment?.status === 'pending_invitation' ? (
                          <span className={styles.occupantPending}>
                            {t('unifiedTeamTab.pendingInvitee', {
                              defaultValue: 'Pending: {{name}}',
                              name: slot.openAssignment.userDisplayName || 'invited user',
                            })}
                          </span>
                        ) : slot.linkedUserGuid || slot.manualName ? (
                          <span className={styles.occupant}>{occupantName}</span>
                        ) : (
                          <span className={styles.vacant}>
                            {t('unifiedTeamTab.vacant')}
                          </span>
                        )}
                      </div>

                      {isAdmin && (
                        <div className={styles.slotActions}>
                          {/* Single unified edit affordance. Vacant slots
                              get the filled-accent variant so the eye is
                              drawn to the actionable row. Occupied slots
                              get the secondary icon-button style. */}
                          {slot.linkedUserGuid || slot.manualName ? (
                            <button
                              type="button"
                              className={styles.editRoleIconBtn}
                              disabled={isBusy}
                              onClick={() => {
                                setEditingSlot(slot);
                                setIsCreatingSlot(false);
                              }}
                              title={t('unifiedTeamTab.editRoleBtn', { defaultValue: 'Edit Role' }) as string}
                              aria-label={t('unifiedTeamTab.editRoleBtn', { defaultValue: 'Edit Role' }) as string}
                            >
                              ✎
                            </button>
                          ) : (
                            <>
                              <button
                                type="button"
                                className={styles.assignBtnPrimary}
                                disabled={isBusy}
                                onClick={() => {
                                  setEditingSlot(slot);
                                  setIsCreatingSlot(false);
                                }}
                              >
                                {t('unifiedTeamTab.assignBtn', { defaultValue: 'Assign' })}
                              </button>
                              {/* DEV-1215 follow-up — direct delete affordance
                                  for vacant slots. The same delete is reachable
                                  via Assign → Remove Role inside the tray, but
                                  that path hides cleanup intent behind an
                                  assignment door. The × is for the (frequent
                                  during the bundle-cleanup pass) "I just want
                                  this gone" case. */}
                              <button
                                type="button"
                                onClick={() =>
                                  void handleDeleteVacantSlot(
                                    slot.roleSlotGuid,
                                    effectiveLabel,
                                  )
                                }
                                disabled={isBusy}
                                title={t('unifiedTeamTab.deleteVacantTitle', {
                                  defaultValue: 'Delete this vacant role',
                                }) as string}
                                aria-label={t('unifiedTeamTab.deleteVacantTitle', {
                                  defaultValue: 'Delete this vacant role',
                                }) as string}
                                style={{
                                  background: 'transparent',
                                  border: 'none',
                                  color: 'var(--text-muted)',
                                  cursor: isBusy ? 'wait' : 'pointer',
                                  fontSize: 16,
                                  lineHeight: 1,
                                  padding: '4px 8px',
                                  marginInlineStart: 4,
                                }}
                              >
                                ×
                              </button>
                            </>
                          )}
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </section>

          {/* ── DEV-1195 Phase 1: Recently Deleted ───────────────────
              Progressive disclosure: first 3 trashed slots render inline
              so admins see what's there at a glance and can Restore in
              one click. Bulk-trash scenarios (4+) get a "Show all (N)"
              button below the visible list. Hidden entirely when zero
              trashed — avoids visual noise for communities that have
              never deleted anything. Background sweep hard-purges rows
              older than 30 days. */}
          {deletedSlots.length > 0 && (
            <section className={styles.section}>
              <header className={styles.sectionHeader}>
                <h3 className={styles.sectionTitle}>
                  {t('unifiedTeamTab.sectionRecentlyDeleted', {
                    defaultValue: 'Recently deleted',
                  })}
                </h3>
                <span className={styles.sectionCount}>{deletedSlots.length}</span>
              </header>
              <ul className={styles.slotList}>
                {(showAllDeleted ? deletedSlots : deletedSlots.slice(0, 3)).map((slot) => {
                  const trashedAt = slot.deletedAt
                    ? new Date(slot.deletedAt)
                    : null;
                  const trashedAgo = trashedAt
                    ? humanizeAgo(trashedAt)
                    : '';
                  const isBusy = !!restoreBusy[slot.roleSlotGuid];
                  return (
                    <li key={slot.roleSlotGuid} className={styles.slotRow}>
                      <span className={styles.dragHandle} aria-hidden="true" />
                      <div className={styles.slotAvatar} aria-hidden="true">
                        🗑
                      </div>
                      <div className={styles.slotLabelCol}>
                        <span className={styles.slotLabel}>
                          {slot.displayTitle}
                        </span>
                        <span
                          className={styles.slotSubLabel}
                          style={{ opacity: 0.7 }}
                        >
                          {t('unifiedTeamTab.deletedAgo', {
                            defaultValue: 'Trashed {{ago}}',
                            ago: trashedAgo,
                          })}
                        </span>
                      </div>
                      {isAdmin && (
                        <button
                          type="button"
                          className={styles.headerAction}
                          onClick={() => handleRestore(slot.roleSlotGuid)}
                          disabled={isBusy}
                        >
                          {isBusy
                            ? t('common.saving', { defaultValue: 'Saving…' })
                            : t('unifiedTeamTab.restoreBtn', {
                                defaultValue: 'Restore',
                              })}
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
              {deletedSlots.length > 3 && (
                <button
                  type="button"
                  className={styles.headerAction}
                  onClick={() => setShowAllDeleted((v) => !v)}
                  style={{ marginBlockStart: 8, alignSelf: 'flex-start' }}
                >
                  {showAllDeleted
                    ? t('unifiedTeamTab.showLessDeleted', {
                        defaultValue: 'Show less',
                      })
                    : t('unifiedTeamTab.showAllDeleted', {
                        defaultValue: 'Show all ({{count}})',
                        count: deletedSlots.length,
                      })}
                </button>
              )}
            </section>
          )}

          {/* ── Members section ──────────────────────────────────── */}
          {/* DEV-1414 / SCOPING §8.9: display-retired for ORG communities —
              the Membership sub-tab is the one roster surface (no pointer;
              Skip 2026-07-02: "nobody will miss it"). Pool-grant mechanics
              unchanged. Geo keeps the full section. */}
          {!membersDisplayRetired && (
          <section className={styles.section}>
            <header className={styles.sectionHeader}>
              <h3 className={styles.sectionTitle}>
                {t('unifiedTeamTab.sectionMembers')}
              </h3>
              <span className={styles.sectionCount}>{poolGrants.length}</span>
              {isAdmin && memberBundle && (
                <button
                  type="button"
                  className={styles.headerAction}
                  onClick={() => setPoolPickerOpen((v) => !v)}
                  disabled={poolBusy}
                >
                  {poolPickerOpen
                    ? t('common.cancel', { defaultValue: 'Cancel' })
                    : t('unifiedTeamTab.addMemberBtn', { defaultValue: 'Add member' })}
                </button>
              )}
            </header>

            {poolLoading && (
              <div className={styles.statusRow}>{t('unifiedTeamTab.loading')}</div>
            )}
            {poolError && (
              <div className={styles.errorRow}>
                {t('unifiedTeamTab.errorPrefix')}: {poolError}
              </div>
            )}

            {!poolLoading && !poolError && (
              <>
                {poolPickerOpen && (
                  <div className={styles.poolPicker}>
                    <input
                      type="search"
                      className={styles.titleInput}
                      placeholder={t('unifiedTeamTab.poolSearchPlaceholder', { defaultValue: 'Search users by name or email…' }) as string}
                      value={poolUserSearch}
                      onChange={(e) => setPoolUserSearch(e.target.value)}
                      disabled={poolBusy}
                      autoFocus
                    />
                    <ul className={styles.poolUserList}>
                      {filteredPoolUsers.length === 0 ? (
                        <li className={styles.empty}>
                          {poolUserSearch.trim()
                            ? t('unifiedTeamTab.poolNoUsers', { defaultValue: 'No users found.' })
                            : t('unifiedTeamTab.poolStartTyping', { defaultValue: 'Start typing to search platform users…' })}
                        </li>
                      ) : (
                        filteredPoolUsers.map((u) => {
                          const guid = String(u.user_guid || '');
                          return (
                            <li
                              key={guid}
                              className={styles.userRow}
                              // Single-click → commit. No intermediate "pick"
                              // state; clicking the row fires the grant + the
                              // workflow_request invitation inline.
                              onClick={() => handlePoolAdd(guid)}
                              style={{
                                cursor: poolBusy ? 'progress' : 'pointer',
                                opacity: poolBusy ? 0.6 : 1,
                              }}
                              role="button"
                              tabIndex={0}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  handlePoolAdd(guid);
                                }
                              }}
                            >
                              <div className={styles.userName}>
                                {u.display_name || u.name || u.email || guid}
                              </div>
                              {u.email && (
                                <div className={styles.userMeta}>{u.email}</div>
                              )}
                            </li>
                          );
                        })
                      )}
                    </ul>
                    {poolBusy && (
                      <div
                        className={styles.statusRow}
                        style={{ marginBlockStart: 8 }}
                      >
                        {t('common.saving', { defaultValue: 'Saving…' })}
                      </div>
                    )}
                  </div>
                )}

                {poolGrants.length === 0 ? (
                  <div className={styles.empty}>
                    {t('unifiedTeamTab.poolEmpty', { defaultValue: 'No community-pool members yet.' })}
                  </div>
                ) : (
                  <ul className={styles.poolList}>
                    {poolGrants.map((g) => {
                      const u = userByGuid.get(String(g.userGuid || '').toUpperCase());
                      const name =
                        u?.display_name || u?.name || u?.email || g.userGuid || '';
                      // Skip UX feedback 2026-06-02: Member rows need an
                      // avatar. Reuse the slot row's slotAvatarImg / slotAvatar
                      // classes so the visual rhymes with the Active Roles
                      // surface above. Photo first, initials fallback.
                      const photoUrl = mediaSrc(u?.profile_image_url || null);
                      const initials = String(name || '?')
                        .split(/\s+/)
                        .filter(Boolean)
                        .slice(0, 2)
                        .map((w) => w[0]?.toUpperCase() || '')
                        .join('') || '?';
                      return (
                        <li key={g.grantGuid} className={styles.poolRow}>
                          {photoUrl ? (
                            <img
                              src={photoUrl}
                              alt=""
                              className={styles.slotAvatarImg}
                            />
                          ) : (
                            <div className={styles.slotAvatar}>{initials}</div>
                          )}
                          <div className={styles.poolRowMain}>
                            <div className={styles.userName}>{name}</div>
                            {u?.email && (
                              <div className={styles.userMeta}>{u.email}</div>
                            )}
                          </div>
                          {g.status === 'pending' && (
                            <span className={styles.pendingBadge}>
                              {t('unifiedTeamTab.pendingBadge', { defaultValue: 'Pending' })}
                            </span>
                          )}
                          {isAdmin && (
                            <button
                              type="button"
                              className={styles.removeBtn}
                              onClick={() => handlePoolRemove(g)}
                              disabled={poolBusy}
                            >
                              {t('common.remove', { defaultValue: 'Remove' })}
                            </button>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </>
            )}
          </section>
          )}

          {/* DEV-1196: original Catalog Reference section moved to the
              top of the page (above Active Roles) and enhanced with
              per-entry + Add buttons. Search field included. */}
        </>
      )}

      {/* Unified Role tray — handles both create (Add Role) and edit
          (per-row pencil/Assign). Mode picker inside the tray decides
          who fills it (Platform / External / Vacant). Remove button
          inside the tray deletes the slot (with confirm). */}
      {editingSlot && (
        <SlotEditModal
          slot={editingSlot}
          communityGuid={entityGuid}
          catalog={catalog}
          users={securityUsers || []}
          isCreate={isCreatingSlot}
          titleOverride={
            isCreatingSlot
              ? (t('unifiedTeamTab.addRoleBtn', { defaultValue: 'Add Role' }) as string)
              : undefined
          }
          onClose={() => {
            setEditingSlot(null);
            setIsCreatingSlot(false);
          }}
          onSaved={(saved) => {
            setSlots((all) => {
              const exists = all.some((s) => s.roleSlotGuid === saved.roleSlotGuid);
              if (exists) {
                return all.map((s) => (s.roleSlotGuid === saved.roleSlotGuid ? saved : s));
              }
              return [...all, saved];
            });
            setIsCreatingSlot(false);
          }}
          onDeleted={(deletedGuid) => {
            setSlots((all) => all.filter((s) => s.roleSlotGuid !== deletedGuid));
            setIsCreatingSlot(false);
          }}
        />
      )}
    </div>
  );
};

export default UnifiedTeamTab;
